import React, { useEffect, useState } from 'react';
import { DialogModal } from '../../../../../theme/components';
import {
  Box,
  Button,
  FormLabel,
  Stack,
  TextField,
  Typography,
  FormControl,
  CircularProgress,
} from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { ErrorText } from '../../../../../theme/components/Forms';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { postGroup } from '../../../../../redux/groups/actions/thunks';
import { useAppDispatch } from '../../../../../redux/store.model';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { useShowMessage } from '../../../../../util/hooks';

const initialValues = {
  name: '',
};
const validationSchema = Yup.object().shape({
  name: Yup.string().required('freshwatch.store.name_required'),
});
interface AddStoreModalProps {
  showModal: boolean;
  closeModal: () => void;
}

const AddStoreModal = (props: AddStoreModalProps) => {
  const { showModal, closeModal } = props;
  const polyglot = getPolyglot();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const showError = useShowErrorMessage();
  const showSuccess = useShowMessage();

  useEffect(() => {
    formik.resetForm();
  }, []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      formik.setTouched({
        name: true,
      });
      if (!formik.isValid) {
        return;
      }
      setIsSubmitting(true);
      try {
        await dispatch(postGroup(values, undefined, undefined));
        showSuccess(polyglot.t('freshwatch.store.added_successfully'));
      } catch (error: any) {
        showError(polyglot.t('freshwatch.store.failed_to_add'));
      } finally {
        setIsSubmitting(false);
        closeModal();
      }
    },
  });

  return (
    <DialogModal
      open={showModal}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      buttonActions={
        <Box
          display="flex"
          gap="1rem"
          justifyContent="space-around"
          width="100%"
        >
          <Button onClick={closeModal} variant="outlined" fullWidth>
            {polyglot.t('button.cancel')}
          </Button>
          <Button
            type="submit"
            onClick={() => formik.handleSubmit()}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              polyglot.t('button.ok')
            )}
          </Button>
        </Box>
      }
      sx={{
        '& .MuiDialogContent-root': {
          padding: '0 2rem 2rem 2rem',
        },
      }}
    >
      <Stack gap={2}>
        <Typography variant="h3" gutterBottom>
          {polyglot.t('freshwatch.store.add')}
        </Typography>
        <Stack display={'flex'} gap="1rem">
          <form onSubmit={formik.handleSubmit}>
            <FormControl
              fullWidth
              sx={{
                gap: '0.5rem',
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <FormLabel htmlFor="name" required>
                {polyglot.t('freshwatch.store.name')}
              </FormLabel>
              <TextField
                fullWidth
                id="name"
                name="name"
                placeholder={polyglot.t(
                  'freshwatch.store.store_name_placeholder'
                )}
                type="text"
                variant="outlined"
                autoComplete="off"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  formik.touched.name &&
                  formik.errors.name && (
                    <ErrorText>{polyglot.t(formik.errors.name)}</ErrorText>
                  )
                }
              />
            </FormControl>
          </form>
        </Stack>
      </Stack>
    </DialogModal>
  );
};

export default AddStoreModal;
